import { apiDelete, apiGet, apiPatch, apiPost } from './client';
import type {
  CursorPage,
  IpWhitelistRow,
  MerchantCredentialsReveal,
  MerchantDto,
  MerchantListItem,
  PayokPaymentMethod,
} from './types';

export type PayokConfigInput = {
  enabled?: boolean;
  baseUrl?: string | null;
  orderCreatePath?: string | null;
  orderQueryPath?: string | null;
  disbursementCreatePath?: string | null;
  merchantId?: string | null;
  paymentMethodCode?: PayokPaymentMethod | null;
  countryCode?: string | null;
  currency?: string | null;
  language?: string | null;
  depositNotificationUrl?: string | null;
  disbursementNotificationUrl?: string | null;
  returnUrl?: string | null;
  /** Accepts either bare base64 (PAYOK delivery form) or full PEM block. */
  payokPublicKey?: string | null;
};

export type CreateMerchantInput = {
  name: string;
  email: string;
  depositFeeBps?: number;
  disbursementFeeIdr?: number;
  cashinMinAmount?: number;
  cashinMaxAmount?: number;
  cashoutMinAmount?: number;
  cashoutMaxAmount?: number;
  apiAccessEnabled?: boolean;
  depositWebhookUrl?: string | null;
  disbursementWebhookUrl?: string | null;
  payok?: PayokConfigInput;
};

export type PatchMerchantInput = Partial<Omit<CreateMerchantInput, 'name' | 'email'>> & {
  name?: string;
  email?: string;
};

export const merchantsApi = {
  list: (
    params: {
      cursor?: string;
      limit?: number;
      search?: string;
      status?: 'active' | 'suspended';
      api?: 'enabled' | 'disabled';
    } = {},
  ) => {
    const q = new URLSearchParams();
    if (params.cursor) q.set('cursor', params.cursor);
    if (params.limit) q.set('limit', String(params.limit));
    if (params.search) q.set('search', params.search);
    if (params.status) q.set('status', params.status);
    if (params.api) q.set('api', params.api);
    const qs = q.toString();
    return apiGet<CursorPage<MerchantListItem>>(`/api/merchants${qs ? `?${qs}` : ''}`);
  },
  get: (id: string) => apiGet<MerchantDto>(`/api/merchants/${id}`),
  create: (input: CreateMerchantInput) => apiPost<MerchantCredentialsReveal>('/api/merchants', input),
  patch: (id: string, input: PatchMerchantInput) =>
    apiPatch<MerchantDto>(`/api/merchants/${id}`, input),
  suspend: (id: string) => apiPost<MerchantDto>(`/api/merchants/${id}/suspend`),
  unsuspend: (id: string) => apiPost<MerchantDto>(`/api/merchants/${id}/unsuspend`),
  regenerateCredentials: (id: string) =>
    apiPost<MerchantCredentialsReveal>(`/api/merchants/${id}/regenerate-credentials`),
  rotatePayokKeypair: (id: string) =>
    apiPost<{
      data: MerchantDto;
      publicKey: { pem: string; sha256: string; notice: string };
    }>(`/api/merchants/${id}/payok/rotate-keypair`),
  /**
   * Decrypt + return the existing client token. Gated server-side to
   * merchant_admin (own merchant) or internal with `merchants.credentials.manage`.
   * Step-up auth: caller passes their account password, server argon2-verifies
   * before decrypting. 401 on wrong password, 410 if no encrypted blob stored.
   */
  revealCredentials: (id: string, password: string, mfaCode?: string) =>
    apiPost<{ transpayClientId: string; transpayClientToken: string; webhookSecret?: string }>(
      `/api/merchants/${id}/credentials/reveal`,
      { password, ...(mfaCode ? { mfaCode } : {}) },
    ),
  regenerateWebhookSecret: (id: string) =>
    apiPost<{ webhookSecret: string; warning: string }>(
      `/api/merchants/${id}/regenerate-webhook-secret`,
    ),
  // API IP whitelist endpoints on merchant-svc
  listApiIpWhitelist: (merchantId: string) =>
    apiGet<{ rows: IpWhitelistRow[] }>(`/api/merchants/${merchantId}/api-ip-whitelist`),
  addApiIpWhitelist: (merchantId: string, input: { cidr: string; label: string; expiresAt?: string }) =>
    apiPost<IpWhitelistRow>(`/api/merchants/${merchantId}/api-ip-whitelist`, input),
  removeApiIpWhitelist: (merchantId: string, rowId: number) =>
    apiDelete<{ ok: true }>(`/api/merchants/${merchantId}/api-ip-whitelist/${rowId}`),
  /**
   * Per-merchant cash-in payment-method allow-list (internal-managed).
   *   available: platform-enabled catalogue (one toggle each)
   *   enabled:   this merchant's allow-list (null = inherit -> all available)
   *   effective: resolved set actually usable
   */
  getPaymentMethods: (merchantId: string) =>
    apiGet<{ available: string[]; enabled: string[] | null; effective: string[] }>(
      `/api/merchants/${merchantId}/payment-methods`,
    ),
  setPaymentMethods: (merchantId: string, enabled: string[] | null) =>
    apiPatch<{ enabled: string[] | null }>(
      `/api/merchants/${merchantId}/payment-methods`,
      { enabled },
    ),
  /**
   * Enable/disable server-to-server disbursement (money-out via API key) for a
   * merchant. High-risk; gated server-side by `merchants.api_disbursement.manage`.
   */
  setApiDisbursement: (merchantId: string, enabled: boolean) =>
    apiPatch<{ apiDisbursementEnabled: boolean }>(
      `/api/merchants/${merchantId}/api-disbursement`,
      { enabled },
    ),
  /**
   * Set the per-VA-code cash-in fee map: each entry is { bps, fixed } (percentage
   * in basis points + flat whole IDR, both applied). Pass null to clear all (every
   * VA falls back to the percentage depositFeeBps). Gated by merchants.edit.
   */
  setVaFees: (
    merchantId: string,
    vaFees: Record<string, { bps: number; fixed: number }> | null,
  ) =>
    apiPatch<{ vaFees: Record<string, { bps: number; fixed: number }> | null }>(
      `/api/merchants/${merchantId}/va-fees`,
      { vaFees },
    ),
  /**
   * Set the per-account-TYPE cash-out fee map: each entry is { bps, fixed }
   * (percentage in basis points + flat whole IDR, both applied on the amount),
   * keyed by beneficiary account type (BANK / WALLET / BPR). Pass null to clear
   * all (every type falls back to the flat disbursementFeeIdr). Gated by
   * merchants.edit.
   */
  setDisbursementFees: (
    merchantId: string,
    disbursementFees: Record<string, { bps: number; fixed: number }> | null,
  ) =>
    apiPatch<{ disbursementFees: Record<string, { bps: number; fixed: number }> | null }>(
      `/api/merchants/${merchantId}/disbursement-fees`,
      { disbursementFees },
    ),
};
